import * as React from 'react'
import { createPortal } from 'react-dom'
import { Icons } from '@/theme/Images'
import { cn } from '@/lib/utils'

// Simple context to pass dialog state
const DialogContext = React.createContext(null)

// Dialog Root - just provides context
const Dialog = ({ open, onOpenChange, children }) => {
  return <DialogContext.Provider value={{ open, onOpenChange }}>{children}</DialogContext.Provider>
}

// DialogTrigger - passes onClick to child
const DialogTrigger = React.forwardRef(({ asChild, children, ...props }, ref) => {
  if (asChild) {
    return React.cloneElement(React.Children.only(children), { ref, ...props })
  }
  return (
    <button ref={ref} {...props}>
      {children}
    </button>
  )
})
DialogTrigger.displayName = 'DialogTrigger'

// DialogPortal - renders to body
const DialogPortal = ({ children }) => createPortal(children, document.body)

// DialogClose - closes dialog
const DialogClose = React.forwardRef(({ asChild, children, onClick, ...props }, ref) => {
  const { onOpenChange } = React.useContext(DialogContext) || {}
  const handleClick = e => {
    onClick?.(e)
    onOpenChange?.(false)
  }
  if (asChild) {
    return React.cloneElement(React.Children.only(children), { ref, onClick: handleClick, ...props })
  }
  return (
    <button ref={ref} onClick={handleClick} {...props}>
      {children}
    </button>
  )
})
DialogClose.displayName = 'DialogClose'

// DialogOverlay - backdrop
const DialogOverlay = React.forwardRef(({ className, onClick, ...props }, ref) => (
  <div
    ref={ref}
    className={cn(
      'fixed inset-0 z-50 bg-black/80 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0',
      className
    )}
    onClick={onClick}
    {...props}
  />
))
DialogOverlay.displayName = 'DialogOverlay'

// Track how many dialogs are open to handle nested dialogs
let openDialogCount = 0

const lockScroll = () => {
  if (openDialogCount === 1) {
    document.body.setAttribute('data-original-overflow', document.body.style.overflow)
    document.documentElement.setAttribute('data-original-overflow', document.documentElement.style.overflow)
    document.body.style.overflow = 'hidden'
    document.documentElement.style.overflow = 'hidden'
  }
}

const unlockScroll = () => {
  if (openDialogCount === 0) {
    const originalBody = document.body.getAttribute('data-original-overflow') || ''
    const originalHtml = document.documentElement.getAttribute('data-original-overflow') || ''
    document.body.style.overflow = originalBody
    document.documentElement.style.overflow = originalHtml
    document.body.removeAttribute('data-original-overflow')
    document.documentElement.removeAttribute('data-original-overflow')
  }
}

// DialogContent - the main component
const DialogContent = React.forwardRef(({ className, children, ...props }, ref) => {
  const { open, onOpenChange } = React.useContext(DialogContext) || {}

  // Lock body scroll when open - use a counter to handle nested dialogs
  React.useEffect(() => {
    if (open) {
      openDialogCount++
      lockScroll()
    } else {
      openDialogCount = Math.max(0, openDialogCount - 1)
      unlockScroll()
    }

    return () => {
      if (open) {
        openDialogCount = Math.max(0, openDialogCount - 1)
        unlockScroll()
      }
    }
  }, [open])

  // Handle ESC key
  React.useEffect(() => {
    if (!open) return
    const handleEscape = e => {
      if (e.key === 'Escape') onOpenChange?.(false)
    }
    document.addEventListener('keydown', handleEscape)
    return () => document.removeEventListener('keydown', handleEscape)
  }, [open, onOpenChange])

  if (!open) return null

  return (
    <DialogPortal>
      <DialogOverlay onClick={() => onOpenChange?.(false)} />
      <div
        ref={ref}
        role="dialog"
        className={cn(
          'fixed left-[50%] top-[50%] z-50 grid w-full max-w-lg translate-x-[-50%] translate-y-[-50%] gap-4 border bg-background p-6 shadow-lg duration-200 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[state=closed]:slide-out-to-left-1/2 data-[state=closed]:slide-out-to-top-[48%] data-[state=open]:slide-in-from-left-1/2 data-[state=open]:slide-in-from-top-[48%] sm:rounded-lg',
          className
        )}
        onClick={e => e.stopPropagation()}
        {...props}
      >
        {children}
        <button
          onClick={() => onOpenChange?.(false)}
          className="absolute right-4 top-4 rounded-sm opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
        >
          <img src={Icons.closeIcon} className="h-6 w-6" alt="Close" />
          <span className="sr-only">Close</span>
        </button>
      </div>
    </DialogPortal>
  )
})
DialogContent.displayName = 'DialogContent'

const DialogHeader = ({ className, ...props }) => (
  <div className={cn('flex flex-col space-y-1.5 text-center sm:text-left', className)} {...props} />
)
DialogHeader.displayName = 'DialogHeader'

const DialogFooter = ({ className, ...props }) => (
  <div className={cn('flex flex-col-reverse sm:flex-row sm:justify-end sm:space-x-2', className)} {...props} />
)
DialogFooter.displayName = 'DialogFooter'

const DialogTitle = React.forwardRef(({ className, ...props }, ref) => (
  <h2 ref={ref} className={cn('text-lg font-semibold leading-none tracking-tight', className)} {...props} />
))
DialogTitle.displayName = 'DialogTitle'

const DialogDescription = React.forwardRef(({ className, ...props }, ref) => (
  <p ref={ref} className={cn('text-sm text-muted-foreground', className)} {...props} />
))
DialogDescription.displayName = 'DialogDescription'

export {
  Dialog,
  DialogPortal,
  DialogOverlay,
  DialogClose,
  DialogTrigger,
  DialogContent,
  DialogHeader,
  DialogFooter,
  DialogTitle,
  DialogDescription,
}
